import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsAdmin, useCanManageLeague, usePermissions } from '../../../contexts/PermissionsContext';
import LeagueFeatureSettings from '../../../components/admin/features/LeagueFeatureSettings';

const LeagueSettingsPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const canManageLeague = useCanManageLeague(parseInt(leagueId));
  const { isLoading: permissionsLoading, canAccessAdmin } = usePermissions();
  const [league, setLeague] = useState(null);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [enableReminderEmails, setEnableReminderEmails] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchLeague = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await unifiedApi.getLeagueById(leagueId);
      setLeague(data);
      setName(data.name);
      setIsActive(data.isActive);
      setEnableReminderEmails(data.enableReminderEmails ?? false);
      setStartDate(data.startDate ? data.startDate.split('T')[0] : '');
      setEndDate(data.endDate ? data.endDate.split('T')[0] : '');
    } catch (err) {
      setError('Failed to load league details.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  const canEdit = isAdmin || canManageLeague;

  useEffect(() => {
    if (!permissionsLoading && !canAccessAdmin()) {
      navigate('/admin', { replace: true });
    }
  }, [canAccessAdmin, permissionsLoading, navigate]);

  useEffect(() => {
    fetchLeague();
  }, [fetchLeague]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await unifiedApi.updateLeague(leagueId, {
        id: parseInt(leagueId),
        name,
        isActive,
        enableReminderEmails,
        startDate: startDate ? `${startDate}T00:00:00Z` : null,
        endDate: endDate ? `${endDate}T00:00:00Z` : null,
      });
      setSuccess('League settings saved successfully.');
      setLeague(prev => ({ ...prev, name, isActive }));
    } catch (err) {
      setError('Failed to save league settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
          sx={{ mb: 2 }}
        >
          Back to League Admin
        </Button>
        <Typography variant="h4" gutterBottom>
          League Settings {league ? `- ${league.name}` : ''}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <Stack spacing={3}>
          <TextField
            label="League Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            disabled={!canEdit}
            inputProps={{ maxLength: 100 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                color="success"
                disabled={!canEdit}
              />
            }
            label={isActive ? 'Active' : 'Archived'}
          />

          <FormControlLabel
            control={
              <Switch
                checked={enableReminderEmails}
                onChange={(e) => setEnableReminderEmails(e.target.checked)}
                color="primary"
                disabled={!canEdit}
              />
            }
            label="Send Game Reminder Emails"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: -2 }}>
            {enableReminderEmails
              ? 'Players will receive automated reminder emails 5 days and 2 days before games.'
              : 'Reminder emails are disabled for this league.'}
          </Typography>

          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fullWidth
            disabled={!canEdit}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth
            disabled={!canEdit}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Typography variant="body2" color="text.secondary">
            {isActive
              ? 'This league is currently active and visible to members.'
              : 'This league is archived and will not appear in active listings.'}
          </Typography>

          {canEdit && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !name.trim()}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/admin/leagues/${leagueId}`)}
            >
              Cancel
            </Button>
          </Box>
          )}
        </Stack>
      </Paper>

      <LeagueFeatureSettings leagueId={parseInt(leagueId)} canEdit={canEdit} />
    </AdminLayout>
  );
};

export default LeagueSettingsPage;
